import { CastEvent, CastMediaInformation } from 'types/Cast';
import Events from 'utils/Events';
import { PlayerEvents, PlayingMode, SUBTITLE_NONE } from 'types/Player';
import ApplicationConfig from 'providers/useConfig/ApplicationConfig';
import { getLocalStorage } from 'utils/fnStorage';
import { CastLoader } from './CastLoader';

/* global cast */

const TRACKS_CHANGED_DEBOUNCE = 10;

const trackListSignature = (tracks: any[] | undefined): string =>
    (tracks ?? []).map((track) => `${track?.language}|${track?.roles?.[0] ?? track?.kind}`).join(',');

const createInitialMediaInfo = (): CastMediaInformation => ({
    playing: false,
    muted: null,
    volume: 0,
    duration: 0,
    progress: 0,
    title: null,
    image: null,
    loaded: false,
    id: null,
    type: null,
    mode: 'normal',
    channelId: undefined,
});

class CastHandler {
    private callback: (event: CastEvent, data: any) => void;

    private session = null;

    private isCasting = false;

    private deviceName = null;

    private remotePlayer: cast.framework.RemotePlayer = null;

    private remotePlayerControls: cast.framework.RemotePlayerController = null;

    private events = new Events();

    private ignoreUpdateTill = 0;

    private tracksChangedDebounce = TRACKS_CHANGED_DEBOUNCE;

    private hasReportedTrackList = false;

    private mediaInfo: CastMediaInformation = createInitialMediaInfo();

    private castProgress = null;

    private lastKnownProgress: number | null = null;

    private playingMode: PlayingMode = PlayingMode.NORMAL;

    private tokenProvider: (() => Promise<{ access_token: string; expires_in: number }>) | null = null;

    private tokenRefreshTimer: ReturnType<typeof setTimeout> | null = null;

    private notifyCaller = (event: CastEvent, data: any) => {
        if (this.callback) {
            this.callback(event, data);
        }
    };

    private loadLibrary = async () => {
        try {
            await CastLoader.load();
            this.notifyCaller(CastEvent.LOADED, true);
        } catch {
            this.notifyCaller(CastEvent.LOADED, false);
        }
    };

    private setPlayer = (remotePlayer) => {
        this.remotePlayer = remotePlayer;
        this.notifyCaller(CastEvent.PLAYER_CREATED, this.remotePlayer);
    };

    private setPlayerController = (controller) => {
        this.remotePlayerControls = controller;
        this.notifyCaller(CastEvent.CONTROLLER_CREATED, this.remotePlayerControls);

        if (this.remotePlayerControls) {
            this.remotePlayerControls.addEventListener(
                window.cast.framework.RemotePlayerEventType.IS_CONNECTED_CHANGED,
                this.onConnectedChange
            );

            this.remotePlayerControls.addEventListener(
                window.cast.framework.RemotePlayerEventType.CURRENT_TIME_CHANGED,
                this.onCurrentTimeChange
            );
            this.remotePlayerControls.addEventListener(window.cast.framework.RemotePlayerEventType.DURATION_CHANGED, this.onDurationChange);
            this.remotePlayerControls.addEventListener(
                window.cast.framework.RemotePlayerEventType.IS_MEDIA_LOADED_CHANGED,
                this.onMediaLoadedChange
            );
            this.remotePlayerControls.addEventListener(
                window.cast.framework.RemotePlayerEventType.IS_PAUSED_CHANGED,
                this.onIsPausedChange
            );
            this.remotePlayerControls.addEventListener(window.cast.framework.RemotePlayerEventType.IS_MUTED_CHANGED, this.onIsMutedChange);
            this.remotePlayerControls.addEventListener(
                window.cast.framework.RemotePlayerEventType.VOLUME_LEVEL_CHANGED,
                this.onVolumeLevelChanged
            );
            this.remotePlayerControls.addEventListener(
                window.cast.framework.RemotePlayerEventType.MEDIA_INFO_CHANGED,
                this.onMediaInfoChanged
            );
            this.remotePlayerControls.addEventListener(window.cast.framework.RemotePlayerEventType.PLAYER_STATE_CHANGED, (event) => {
                console.log(event.field, event.value);
            });
        }
    };

    private setConnected = (connected: boolean) => {
        this.isCasting = connected;
        if (connected) {
            // Fresh session -> drop any resume position left over from a previous cast.
            this.lastKnownProgress = null;
        }
        if (!connected) {
            this.clearTokenRefreshTimer();
            this.castProgress = null;
            this.playingMode = PlayingMode.NORMAL;
            this.mediaInfo = createInitialMediaInfo();
            this.hasReportedTrackList = false;
        }
        this.notifyCaller(CastEvent.CASTING, this.isCasting);
        this.events.triggerEvents(PlayerEvents.CAST_STATUS_CHANGED, this.isCasting);
    };

    private clearTokenRefreshTimer = () => {
        if (this.tokenRefreshTimer) {
            clearTimeout(this.tokenRefreshTimer);
            this.tokenRefreshTimer = null;
        }
    };

    private scheduleTokenRefresh = (expiresIn: number) => {
        this.clearTokenRefreshTimer();

        if (!this.tokenProvider || !this.isCasting) {
            return;
        }

        const bufferSeconds = 30;
        const minDelaySeconds = 10;
        const delaySeconds = Math.max(minDelaySeconds, expiresIn - bufferSeconds);

        this.tokenRefreshTimer = setTimeout(async () => {
            this.tokenRefreshTimer = null;

            if (!this.tokenProvider || !this.isCasting) {
                return;
            }

            try {
                const token = await this.tokenProvider();
                if (!token?.access_token) {
                    return;
                }

                if (token.expires_in > bufferSeconds) {
                    await this.sendAccessToken(token.access_token, token.expires_in);
                }
                this.scheduleTokenRefresh(token.expires_in);
            } catch (e) {
                console.error('[Cast] token refresh failed', e);
            }
        }, delaySeconds * 1000);
    };

    setTokenProvider = (fn: () => Promise<{ access_token: string; expires_in: number }>) => {
        this.tokenProvider = fn;
    };

    private setReceiverName = (receiverName: string) => {
        this.deviceName = receiverName;
        this.notifyCaller(CastEvent.RECEIVER_NAME, this.deviceName);
    };

    private onCurrentTimeChange = (data: cast.framework.RemotePlayerChangedEvent) => {
        if (this.mediaInfo) {
            this.mediaInfo = {
                ...this.mediaInfo,
                progress: data?.value,
            };

            if (data?.value !== undefined && data.value > 0) {
                this.castProgress = data.value;
                this.lastKnownProgress = data.value;
            }
        }

        this.notifyCaller(CastEvent.MEDIA_INFO_CHANGED, this.mediaInfo);
        this.events.triggerEvents(PlayerEvents.TIME_UPDATE, this.mediaInfo.progress);
    };

    private onDurationChange = (data: cast.framework.RemotePlayerChangedEvent) => {
        if (this.mediaInfo) {
            this.mediaInfo = {
                ...this.mediaInfo,
                duration: data?.value,
            };
        }

        this.notifyCaller(CastEvent.MEDIA_INFO_CHANGED, this.mediaInfo);
        this.events.triggerEvents(PlayerEvents.TIME_UPDATE, this.mediaInfo.progress);
    };

    private onMediaLoadedChange = (data: cast.framework.RemotePlayerChangedEvent) => {
        this.mediaInfo = {
            ...this.mediaInfo,
            loaded: data?.value ?? false,
        };

        this.notifyCaller(CastEvent.MEDIA_INFO_CHANGED, this.mediaInfo);
    };

    private onIsPausedChange = (data: cast.framework.RemotePlayerChangedEvent) => {
        if (this.mediaInfo) {
            this.mediaInfo = {
                ...this.mediaInfo,
                playing: data?.value === false,
            };
        }

        this.notifyCaller(CastEvent.MEDIA_INFO_CHANGED, this.mediaInfo);
        this.events.triggerEvents(this.mediaInfo.playing ? PlayerEvents.PLAYING : PlayerEvents.PAUSE, {
            type: this.mediaInfo.playing ? 'play' : 'pause',
        });
    };

    private onIsMutedChange = (data: cast.framework.RemotePlayerChangedEvent) => {
        if (this.mediaInfo) {
            this.mediaInfo = {
                ...this.mediaInfo,
                muted: data?.value,
            };
        }

        this.notifyCaller(CastEvent.MEDIA_INFO_CHANGED, this.mediaInfo);
        this.events.triggerEvents(PlayerEvents.VOLUME_CHANGE, this.mediaInfo.volume);
    };

    private onVolumeLevelChanged = (data: cast.framework.RemotePlayerChangedEvent) => {
        if (this.mediaInfo) {
            this.mediaInfo = {
                ...this.mediaInfo,
                volume: data?.value,
            };
        }

        this.notifyCaller(CastEvent.MEDIA_INFO_CHANGED, this.mediaInfo);
        this.events.triggerEvents(PlayerEvents.VOLUME_CHANGE, this.mediaInfo.volume);
    };

    private onMediaInfoChanged = (data: cast.framework.RemotePlayerChangedEvent) => {
        if (!data || !data.value || !data.value.metadata) {
            // do nothing
        } else {
            if (
                data.value.contentType === 'LIVE' &&
                this.mediaInfo.type === data.value.contentType &&
                data.value.metadata.title !== this.mediaInfo.title
            ) {
                this.events.triggerEvents(PlayerEvents.ENDED, null);
            }
            this.playingMode = data.value.customData.playMode as PlayingMode;
            const nextImage = data.value.metadata.images?.[0]?.url ?? this.mediaInfo.image;
            this.mediaInfo = {
                ...this.mediaInfo,
                title: data.value.metadata.title,
                image: nextImage,
                id: data.value.contentId,
                type: data.value.contentType,
                mode: data.value.customData.playMode,
                channelId: data.value.channelId,
            };
        }

        this.notifyCaller(CastEvent.MEDIA_INFO_CHANGED, this.mediaInfo);
    };

    private onMessage = (namespace, msg) => {
        const message = JSON.parse(msg);

        if (message && message?.type === 'tooManyDevices') {
            this.events.triggerEvents(PlayerEvents.TOO_MANY_DEVICES, null);
            this.notifyCaller(CastEvent.TOO_MANY_DEVICES, null);
            this.disconnect().catch(() => {});
            return;
        }

        if (message && message?.type === 'sessionLimit') {
            this.notifyCaller(CastEvent.SESSION_LIMIT, null);
            this.disconnect().catch(() => {});
            return;
        }

        if (message && (message?.type === 'streamTakeover' || message?.error === 'SESSION_EXPIRED')) {
            this.notifyCaller(CastEvent.STREAM_TAKEOVER, null);
            this.disconnect().catch(() => {});
            return;
        }

        if (message && message?.type === 'event') {
            if (message?.event?.type === 'ended') {
                // console.log('onMessage', message);
                this.events.triggerEvents(PlayerEvents.ENDED, null);
                this.mediaInfo = {
                    ...this.mediaInfo,
                    loaded: false,
                };
                this.notifyCaller(CastEvent.MEDIA_INFO_CHANGED, this.mediaInfo);
            }

            if (message?.event?.type === 'timeupdate') {
                this.events.triggerEvents(PlayerEvents.TIME_UPDATE, null);
            }
        }

        if (this.ignoreUpdateTill > Date.now()) {
            return;
        }

        if (message && message.type === 'update') {
            if (message.update) {
                if (message?.update?.player && message.update.player?.isBuffering != null) {
                    this.events.triggerEvents(PlayerEvents.BUFFERING, {
                        buffering: message.update.player.isBuffering,
                    });
                }

                if (message.update?.video?.paused != null) {
                    this.mediaInfo = {
                        ...this.mediaInfo,
                        playing: !message.update.video.paused,
                    };

                    this.events.triggerEvents(this.mediaInfo.playing ? PlayerEvents.PLAYING : PlayerEvents.PAUSE, {
                        type: this.mediaInfo.playing ? 'play' : 'pause',
                    });
                }

                const audiosBefore = trackListSignature(this.mediaInfo?.audioLanguages);
                const subtitlesBefore = trackListSignature(this.mediaInfo?.subtitleLanguages);
                const selectedAudioBefore = this.mediaInfo.selectedAudio;
                const selectedSubtitleBefore = this.mediaInfo.selectedSubtitle;

                if (message.update?.player?.getAudioLanguages) {
                    const selectedTrack = message.update.player?.getVariantTracks.find((track) => track.active === true);

                    this.mediaInfo = {
                        ...this.mediaInfo,
                        audioLanguages: message.update.player.getVariantTracks,
                        selectedAudio: {
                            language: selectedTrack?.language,
                            channelsCount: selectedTrack?.channelsCount,
                            role: selectedTrack?.roles?.[0],
                            label: null,
                            raw: selectedTrack,
                        },
                    };
                }

                if (message.update?.player?.getTextTracks) {
                    const selectedTrack = message.update.player?.getTextTracks.find((track) => track.active === true);

                    this.mediaInfo = {
                        ...this.mediaInfo,
                        subtitleLanguages: message.update.player.getTextTracks,
                        selectedSubtitle: !message.update.player.isTextTrackVisible
                            ? { role: null, label: null, raw: null, language: SUBTITLE_NONE }
                            : {
                                  language: selectedTrack?.language,
                                  channelsCount: null,
                                  role: selectedTrack?.roles?.[0],
                                  label: null,
                                  raw: selectedTrack,
                              },
                        isSubtitleVisible: message.update.player.isTextTrackVisible,
                    };
                }

                if (
                    trackListSignature(this.mediaInfo.audioLanguages) !== audiosBefore ||
                    trackListSignature(this.mediaInfo.subtitleLanguages) !== subtitlesBefore ||
                    this.mediaInfo.selectedAudio?.language !== selectedAudioBefore?.language ||
                    this.mediaInfo.selectedAudio?.role !== selectedAudioBefore?.role ||
                    this.mediaInfo.selectedSubtitle?.language !== selectedSubtitleBefore?.language ||
                    this.mediaInfo.selectedSubtitle?.role !== selectedSubtitleBefore?.role
                ) {
                    const isFirstTrackList = !this.hasReportedTrackList && (this.mediaInfo.subtitleLanguages?.length ?? 0) > 0;

                    if (!isFirstTrackList) {
                        this.tracksChangedDebounce -= 1;
                    }

                    const willFire = isFirstTrackList || this.tracksChangedDebounce === 0;

                    if (willFire) {
                        this.hasReportedTrackList = true;
                        this.events.triggerEvents(PlayerEvents.TRACKS_CHANGED, null);
                        this.tracksChangedDebounce = TRACKS_CHANGED_DEBOUNCE;
                    }
                }
            }
        }
    };

    private onConnectedChange = (data: cast.framework.RemotePlayerChangedEvent) => {
        if (data.value) {
            this.setConnected(true);
            this.session = window.cast.framework.CastContext.getInstance().getCurrentSession();

            if (this.session) {
                this.setReceiverName(this.session.getSessionObj().receiver.friendlyName);
                this.session.addMessageListener(ApplicationConfig.app_config.chromecast.namespace, this.onMessage);
            }
        } else {
            this.setConnected(false);
        }
    };

    private onSessionStateChange = (data: cast.framework.SessionStateEventData) => {
        if (
            data.sessionState === window.cast.framework.SessionState.SESSION_RESUMED ||
            data.sessionState === window.cast.framework.SessionState.SESSION_STARTED
        ) {
            this.setConnected(true);
        }

        if (data.sessionState === window.cast.framework.SessionState.SESSION_ENDED) {
            this.setReceiverName(null);
            this.setConnected(false);
        }
    };

    private initConnection = () => {
        if (window.chrome && window.chrome.cast && window.cast) {
            window.cast.framework.CastContext.getInstance().setOptions({
                receiverApplicationId: ApplicationConfig.app_config.chromecast.receiver_id,
                autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
                resumeSavedSession: true,
            });

            const remotePlayer = new window.cast.framework.RemotePlayer();
            this.setPlayer(remotePlayer);

            const controller = new window.cast.framework.RemotePlayerController(remotePlayer);
            this.setPlayerController(controller);

            window.cast.framework.CastContext.getInstance().addEventListener(
                window.cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
                this.onSessionStateChange
            );
        }
    };

    init = async (callback: (event: CastEvent, data: any) => void) => {
        this.callback = callback;

        if (ApplicationConfig.isFeatureActive('google_cast') && ApplicationConfig.app_config.chromecast.receiver_id) {
            await this.loadLibrary();
            this.initConnection();
        }
    };

    notify = () => {
        this.notifyCaller(CastEvent.CASTING, this.isCasting);
        this.notifyCaller(CastEvent.RECEIVER_NAME, this.deviceName);
        this.notifyCaller(CastEvent.LOADED, true);
    };

    syncUILanguage = (language: string) => {
        this.sendMessage({
            type: 'setLanguage',
            value: language || getLocalStorage('appLanguage'),
        }).catch(() => {});
    };

    killListeners = () => {
        this.events.removeAllListeners();
    };

    toggleConnect = async () => {
        try {
            const ctx = window.cast.framework.CastContext.getInstance();
            await ctx.requestSession();
        } catch {
            this.session = null;
        }
    };

    disconnect = async () => {
        try {
            const castSession = window.cast.framework.CastContext.getInstance().getCurrentSession();
            if (castSession) {
                await castSession.endSession(true);
            }
        } catch (e) {
            console.warn('Failed to disconnect from cast:', e);
        }
    };

    sendMediaMessage = async (message: any) => {
        const castSession = window.cast.framework.CastContext.getInstance().getCurrentSession();

        if (castSession) {
            return castSession.sendMessage(ApplicationConfig.app_config.chromecast.media_namespace, JSON.stringify(message));
        }
        return Promise.reject(new Error('No active session'));
    };

    sendMessage = async (message: any) => {
        const castSession = window.cast.framework.CastContext.getInstance().getCurrentSession();

        if (castSession) {
            return castSession.sendMessage(ApplicationConfig.app_config.chromecast.namespace, JSON.stringify(message));
        }
        return Promise.reject(new Error('No active session'));
    };

    loadMedia = async (request: chrome.cast.media.LoadRequest) => {
        // The receiver's custom-namespace channel can lag behind the session connecting. Thats why we try retry
        const maxAttempts = 3;
        const retryDelayMs = 400;

        for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
            try {
                // eslint-disable-next-line no-await-in-loop
                await this.sendMessage({
                    type: 'loadMedia',
                    data: request,
                });

                this.ignoreUpdateTill = -1;
                this.hasReportedTrackList = false;
                this.tracksChangedDebounce = TRACKS_CHANGED_DEBOUNCE;
                this.events.triggerEvents(PlayerEvents.MEDIA_CHANGED, null);

                const expiresIn = (request?.media?.customData as { expiresIn?: number })?.expiresIn;
                if (expiresIn && expiresIn > 0) {
                    this.scheduleTokenRefresh(expiresIn);
                }
                return;
            } catch (e) {
                const isTransient = e === 'invalid_parameter' || (e as Error)?.message === 'No active session';

                if (!isTransient || attempt === maxAttempts) {
                    console.error('load failed', e);
                    return;
                }

                console.warn(`load failed (attempt ${attempt}/${maxAttempts}), retrying`, e);
                // eslint-disable-next-line no-await-in-loop
                await new Promise((resolve) => {
                    setTimeout(resolve, retryDelayMs * attempt);
                });
            }
        }
    };

    sendCacheKey = (cacheKey: string) =>
        this.sendMessage({
            type: 'updateCacheKey',
            value: cacheKey,
        }).catch(() => {});

    sendAccessToken = (accessToken: string, expiresIn: number) =>
        this.sendMessage({
            type: 'accessToken',
            value: { accessToken, expiresIn },
        }).catch(() => {});

    getMediaInfo = () => this.mediaInfo;

    getRemotePlayer = () => this.remotePlayer;

    getRemotePlayerController = () => this.remotePlayerControls;

    isCast = () => this.isCasting;

    addEventListener = (event: string, callback: (object?: {}) => void) => this.events.addEventListener(event, callback);

    removeEventListener = (event: string, callback: (object?: {}) => void) => this.events.removeEventListener(event, callback);

    setIgnoreUpdateTill = (till: number) => {
        this.ignoreUpdateTill = Date.now() + till;
    };

    resetTrackChangeDebounce = () => {
        this.tracksChangedDebounce = TRACKS_CHANGED_DEBOUNCE;
    };

    getCastProgress = () => {
        const returnValue = this.castProgress;
        this.castProgress = null;

        return returnValue;
    };

    // Last position the receiver reported, preserved across disconnect so local playback can resume where casting left off.
    consumeLastKnownProgress = () => {
        const returnValue = this.lastKnownProgress;
        this.lastKnownProgress = null;
        return returnValue;
    };

    resetCastProgress = () => {
        this.castProgress = null;
    };

    getPlayMode = () => {
        const returnValue = this.playingMode;
        this.playingMode = PlayingMode.NORMAL;

        return returnValue;
    };
}

export default new CastHandler();
